import React, { useState, useCallback, forwardRef, useEffect, useRef } from 'react'
import { useGLTF } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import * as THREE from 'three'

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('/draco/');

const DownloadModel = forwardRef(({ 
  downloadButtonColor, 
  hoverColor, 
  stationVisible, 
  data, 
  setIsLoading,
  onLoad,
  ...props 
}, ref) => {
  const { nodes, materials } = useGLTF('/models/Seperated/7_10-buttons-transformed.glb', true, true, (loader) => {
    loader.setDRACOLoader(dracoLoader);
  });
  const [isHovering, setIsHovering] = useState(false)
  const groupRef = useRef()
  const outerMeshRef = useRef()
  const innerMeshRef = useRef()

  useEffect(() => {
    if (onLoad) onLoad();
  }, [onLoad]);

  useFrame(() => {
    if (outerMeshRef.current && innerMeshRef.current) {
      const outerTargetColor = isHovering ? new THREE.Color('white') : new THREE.Color(downloadButtonColor)
      const innerTargetColor = isHovering ? new THREE.Color(downloadButtonColor) : new THREE.Color('white')
      
      outerMeshRef.current.material.color.lerp(outerTargetColor, 0.1)
      innerMeshRef.current.material.color.lerp(innerTargetColor, 0.1)
    }
  })

  const handleDownload = useCallback(async () => {
    setIsLoading(true);

    const downloadUrl = `https://pfizer-vwt.s3-accelerate.amazonaws.com/assets/Booth_${stationVisible + 1}/download.pptx`;
    const stationTitle = data.stations[stationVisible].title;
    const sanitizedTitle = stationTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `Pfizer_${sanitizedTitle}.pptx`;

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const file = new Blob([blob], {type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'});
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);

      await logAction('click', 'download');
    } catch (error) {
      console.error('Error downloading the file:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  }, [stationVisible, data, setIsLoading]);

  const logAction = async (action, value) => {
    const email = localStorage.getItem('userEmail');
    const stationName = data.stations[stationVisible].title;
    const apiUrl = `https://vwt-api.pfizer-qr.com/api/?e=${encodeURIComponent(email)}&c=website&a=${action}&l=${encodeURIComponent(stationName)}&v=${value}`;
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      console.log(`${action} action logged successfully`);
    } catch (error) {
      console.error(`Error logging ${action} action:`, error);
    }
  };

  return (
    <group 
      {...props} 
      ref={groupRef}
      onClick={handleDownload}
      onPointerOver={() => {
        setIsHovering(true)
        document.body.style.cursor = 'pointer'
      }}
      onPointerOut={() => {
        setIsHovering(false)
        document.body.style.cursor = 'default'
      }}
    >
      <group position={[-0.017, 5.885, -0.007]}>
        <mesh 
          ref={outerMeshRef}
          geometry={nodes.Download_Buttons003.geometry} 
          material={materials['Download button blue']}
        >
          <meshStandardMaterial color={downloadButtonColor} />
        </mesh>
        <mesh 
          ref={innerMeshRef}
          geometry={nodes.Download_Buttons003_1.geometry} 
          material={materials['Download button white']}
        >
          <meshStandardMaterial color="white" />
        </mesh>
      </group>
    </group>
  )
})

useGLTF.preload('/models/Seperated/7_10-buttons-transformed.glb')

export default DownloadModel;